import { memo, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IoArrowForward } from 'react-icons/io5';
import { MdLocationOn } from 'react-icons/md';
import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import styles from './Card.module.css';
import { priceData } from '../../../../containers/ListingPage/ListingPage.shared';
import { toggleFavoriteThunk, isFavorite as isFavoriteSelector } from '../../../../ducks/favorites.duck';

const categoryColors = {
  "Auto-Motorrad": {
    color: "#FFFFFF",
    background: "#054cf199" // dark slate
  },

  "Elektronik-Technik": {
    color: "#1E3A8A",
    background: "#DBEAFE" // tech blue
  },

  "Events": {
    color: "#7C2D12",
    background: "#FFEDD5" // festive orange
  },

  "Familie-Kind-Baby": {
    color: "#9D174D",
    background: "#FCE7F3" // soft pink
  },

  "Haus-Garten": {
    color: "#065F46",
    background: "#D1FAE5" // green nature
  },

  "Maschinen-Geraete": {
    color: "#111827",
    background: "#E5E7EB" // industrial gray
  },

  "Mode-Beauty": {
    color: "#701A75",
    background: "#FAE8FF" // fashion purple
  },

  "Musik-Unterhaltung": {
    color: "#312E81",
    background: "#E0E7FF" // creative indigo
  },

  "Sonstiges": {
    color: "#374151",
    background: "#F3F4F6" // neutral
  },

  "Spiele-Konsolen": {
    color: "#064E3B",
    background: "#A7F3D0" // playful green
  },

  "Sport": {
    color: "#7C2D12",
    background: "#FDE68A" // energetic yellow
  },

  "Urlaub-Reisen": {
    color: "#075985",
    background: "#E0F2FE" // sky blue
  },

  "Wellness": {
    color: "#14532D",
    background: "#DCFCE7" // calm wellness green
  },

  "Werkstatt-Werkzeug": {
    color: "#1F2937",
    background: "#FEF3C7" // workshop yellow
  }
};




// Unit type labels
const UNIT_TYPE_LABELS = {
  day: 'pro Tag',
  hour: 'pro Stunde',
  lesson: 'pro Stunde'
};

const Card = memo(({ listing, onClick,keyValuePairsOfEnhancedCategories,config,intl }) => {
  // Early return for invalid listing
  if (!listing?.attributes) {
    return null;
  }
  const { attributes, images, id } = listing;
  const { title, price, publicData } = attributes;
  const { listingType, unitType } = publicData || {};
  const { color, background } = categoryColors[publicData.categoryLevel1] ||{ color: '#374151', background: '#F3F4F6' };

  // Redux hooks
  const dispatch = useDispatch();
  const isListingFavorite = useSelector(state => isFavoriteSelector(state, id.uuid));
  const currentUser = useSelector(state => state.user.currentUser);
  const toggleInProgress = useSelector(state => state.favorites.toggleInProgress);
  
  // Local loading state for this specific card
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // Memoized computed values
  const imageUrl = useMemo(() => {
    return images?.[0]?.attributes?.variants?.['listing-card']?.url ||
           images?.[0]?.attributes?.variants?.['scaled-small']?.url ||
           'https://via.placeholder.com/400x300?text=No+Image';
  }, [images]);

  const formattedPrice = useMemo(() => {
    if (!price?.amount) return null;
    const  { formattedPrice:unitprice } = priceData(price, config.currency, intl);
    return unitprice;
    // const symbol = getCurrencySymbol(price.currency);
    // return `${symbol}${unitprice}`;
  }, [price]);

  const location = useMemo(() => {
    return publicData?.location?.address || 'Location not specified';
  }, [publicData]);



const categoryLabel = useMemo(() => {
    if (publicData?.categoryLevel1) {
      return keyValuePairsOfEnhancedCategories[publicData.categoryLevel1] || publicData.categoryLevel1;
    }}, [publicData, keyValuePairsOfEnhancedCategories]);
  // Handle card click
  const handleClick = () => {
    if (onClick && id?.uuid) {
      onClick(id.uuid);
    }
  };

  // Handle image error
  const handleImageError = (e) => {
    e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
  };

  // Handle favorite toggle
  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    
    if (!currentUser) {
      // Redirect to login or show message
      window.location.href = '/login';
      return;
    }

    if (isTogglingFavorite || toggleInProgress === id.uuid) {
      return; // Prevent double clicks
    }

    setIsTogglingFavorite(true);
    try {
      await dispatch(toggleFavoriteThunk(id.uuid));
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const isLoading = isTogglingFavorite || toggleInProgress === id.uuid;

  return (
    <div className={styles.card} onClick={handleClick} role="button" tabIndex={0}>
      {/* Image Container */}
      <div className={styles.imageContainer}>
        <img
          src={imageUrl}
          alt={title || 'Listing image'}
          className={styles.image}
          onError={handleImageError}
          loading="lazy"
        />
        
        {/* Favorite Heart Icon */}
        <button 
          className={styles.favoriteButton}
          onClick={handleFavoriteClick}
          aria-label={isListingFavorite ? 'Remove from favorites' : 'Add to favorites'}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className={styles.favoriteLoader}></div>
          ) : isListingFavorite ? (
            <AiFillHeart className={styles.heartIconFilled} />
          ) : (
            <AiOutlineHeart className={styles.heartIcon} />
          )}
        </button>
        
        {/* Listing Type Tag */}
        {listingType && (
          <span className={styles.tag } title={categoryLabel} style={{ color, backgroundColor:background }}>
            {categoryLabel}
          </span>
        )}
      </div>

      {/* Card Content Overlay */}
      <div className={styles.cardContent}>
        {/* Title */}
        <h3 className={styles.title} title={title}>
          {title || 'No Title'}
        </h3>

        {/* Location */}
        <div className={styles.locationContainer}>
          <MdLocationOn className={styles.locationIcon} size={18} />
          <span className={styles.location}>{location}</span>
        </div>

        {/* Bottom Section: Price and Action */}
        <div className={styles.bottomSection}>
          {/* Price Badge */}
          {formattedPrice && (
            <div className={styles.priceBadge}>
              <span className={styles.priceAmount}>{formattedPrice}</span>
              {unitType && (
                <span className={styles.priceUnit}>{UNIT_TYPE_LABELS?.[unitType]}</span>
              )}
            </div>
          )}

          {/* View Button */}
          <button 
            className={styles.viewButton}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            aria-label="View details"
          >
            <IoArrowForward size={20} />
          </button>
        </div>
      </div>
    </div>
  );
});

Card.displayName = 'Card';

export default Card;